import Research from "../Research"
import util from "@/util"
import globalState from "@/store/globalState"
import { unref } from 'vue'
import Employee from "@/employee/Employee"
import { list, object, reference, RefLookupFunction, serializable, serialize } from "serializr"
import ValueRange from "@/effects/ValueRange"
import ValueMod from "@/effects/ValueMod"
import Farm from "@/farm/Farm"

export default class Laboratory {
  private farm: Farm

  @serializable(list(object(Research)))
  private allNodes: Research[] = []

  @serializable(object(Research))
  private _currentResearch: Research | undefined = undefined

  public researchers: Employee[] = []
  public debug = false

  // @ts-ignore
  constructor(treedata: any = { nodes: [] }, farm: Farm = undefined) {
    this.farm = farm

    for (let node of treedata.nodes) {
      this.allNodes.push(new Research(node))
    }
  }

  load(state: any) {
    // Our constructor has created a list of research nodes
    for (let i = 0; i < state.allNodes.length; i++) {
      // Go through all the new nodes and load their states into the state of ours.
      // This will not touch new research values that ours might have.
      let ourResearch = this.researchById(state.allNodes[i].id)
      if (ourResearch !== undefined) {
        ourResearch.load(state.allNodes[i])
      }
    }

    // Load current research data and but link it to the correct reference
    if (state._currentResearch != undefined) {
      this._currentResearch = this.researchById(state._currentResearch.id)
    }
  }

  update(dt: number, assigned: Employee[]) {
    this.researchers = assigned

    if (this._currentResearch) {
      this._currentResearch.research(this.labPower * dt)

      // Research is completed
      if (this._currentResearch.completion.fraction >= 1) {
        this.farm.notifyResearchCompletion(this._currentResearch)
        this._currentResearch = undefined
      }
    }
  }

  startResearch(research: Research) {
    if (this.available.includes(research)) {
      // If we are already researching something, mark that research as paused
      if (this._currentResearch) this._currentResearch.isBeingResearched = false
      // Start the new research
      this._currentResearch = research
      this._currentResearch.isBeingResearched = true
    }
  }

  getResearchCost(research: Research): number {
    return research.researchCost.value
  }

  get labPower(): number {
    let sum = 0
    for (let r of this.researchers) {
      let uselessCutoff = 0.3
      let contribution = (r.iq.fraction - uselessCutoff) * r.constants['researchMul']
      //if (r.iqStat.fraction < uselessCutoff) contribution = -contribution
      // Adjust total value based on mood
      contribution *= r.moodIqModifier

      sum += contribution
    }
    if (this.debug) sum = 1000

    return sum * 10
  }

  get completed(): Research[] {
    let completed: Research[] = []
    for (let node of this.allNodes) {
      if (node.isCompleted) {
        completed.push(node)
      }
    }
    return completed
  }

  get available(): Research[] {
    let completed = this.completed
    let available = []
    // Check all nodes
    for (let node of this.allNodes) {
      // If a node is not completed, it might be available
      if (!node.isCompleted) {
        // But only if all of its requirements ARE completed.
        let i = 0
        // Count how many requirements are fulfilled
        for (let requirement of node.requires) {
          if (completed.find(research => research.id == requirement)) {
            i += 1
          }
        }
        // Check if all are completed
        if (i == node.requires.length) available.push(node)
      }
    }

    return available
  }

  get currentResearch(): Research | undefined {
    return this._currentResearch
  }

  get currentResearchProgress(): ValueRange {
    if (this._currentResearch == undefined) {
      return new ValueRange(0, 0, 1)
    }
    return this._currentResearch.completion
  }

  availableForEmployees(): Research[] {
    let available: Research[] = []
    for (let research of this.completed) {
      if (research.target === "employee") {
        available.push(research)
      }
    }
    return available
  }

  availableForFarm(): Research[] {
    let available: Research[] = []
    for (let research of this.completed) {
      if (research.target === "farm") {
        available.push(research)
      }
    }
    return available
  }

  researchById(id: string): Research | undefined {
    return this.allNodes.find(research => research.id === id)
  }

  getResearchIndex(research: Research): number {
    for (let [i, r] of this.allNodes.entries()) {
      if (r.id == research.id) return i
    }
    return 0
  }

  researchIsVisible(research: Research) {
    let visibles: Research[] = this.available.concat(this.completed)

    for (let r of visibles) {
      if (r.id == research.id) return true
    }

    return false
  }

  toJSON() {
    let s = serialize(this)
    return s
  }
}
